import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Dices, 
  Hash,
  Play,
  Shuffle,
  Calculator,
  User,
  HelpCircle,
  ArrowRight,
  Sparkles,
  MapPin,
  Moon,
  Clock,
  RotateCcw,
  Sun,
} from 'lucide-react';

const ToolsGridModern = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const toolsList = [
    {
      id: 'birthchart',
      title: 'Birth Chart',
      subtitle: 'Astrological chart',
      description: 'Generate your complete birth chart with planetary positions',
      icon: Sparkles,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/birthchart',
      category: 'Astrology'
    },
    {
      id: 'ascendant',
      title: 'Ascendant Calculator',
      subtitle: 'Rising sign',
      description: 'Calculate your ascendant and rising sign',
      icon: MapPin,
      color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      path: '/ascendant',
      category: 'Astrology'
    },
    {
      id: 'moonphase',
      title: 'Moon Phase',
      subtitle: 'Lunar cycles',
      description: 'Calculate moon phases for any date and time',
      icon: Moon,
      color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      path: '/moonphase',
      category: 'Astrology'
    },
    {
      id: 'planetaryhours',
      title: 'Planetary Hours',
      subtitle: 'Magical timing',
      description: 'Calculate planetary hours for optimal spell timing',
      icon: Clock,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/planetaryhours',
      category: 'Astrology'
    },
    {
      id: 'randomtarotcard',
      title: 'Tarot Card',
      subtitle: 'Daily guidance',
      description: 'Draw a tarot card for daily insight',
      icon: Dices,
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      path: '/tools/randomtarotcard',
      category: 'Tarot'
    },
    {
      id: 'randomrune',
      title: 'Rune',
      subtitle: 'Ancient wisdom',
      description: 'Draw a rune for Norse guidance',
      icon: Hash,
      color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      path: '/tools/randomrune',
      category: 'Runes'
    },
    {
      id: 'randomplayingcard',
      title: 'Playing Card',
      subtitle: 'Classic divination',
      description: 'Traditional playing card reading',
      icon: Play,
      color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      path: '/tools/randomplayingcard',
      category: 'Cards'
    },
    {
      id: 'randomnumber',
      title: 'Number',
      subtitle: 'Numerical guidance',
      description: 'Get a number for decisions',
      icon: Shuffle,
      color: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      path: '/tools/randomnumber',
      category: 'Numerology'
    },
    {
      id: 'randomcoinflipper',
      title: 'Coin Flipper',
      subtitle: 'Quick decisions',
      description: 'Flip a coin for yes/no answers',
      icon: Shuffle,
      color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      path: '/tools/randomcoinflipper',
      category: 'Tarot'
    },
    {
      id: 'numerologycalculator',
      title: 'Birthday Numerology',
      subtitle: 'Life path number',
      description: 'Calculate your life path number',
      icon: Calculator,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/tools/numerologycalculator',
      category: 'Numerology'
    },
    {
      id: 'numerologyname',
      title: 'Name Numerology',
      subtitle: 'Name analysis',
      description: 'Discover your name\'s numerological meaning',
      icon: User,
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      path: '/tools/numerologyname',
      category: 'Numerology'
    },
    {
      id: 'yesorno',
      title: 'Yes or No',
      subtitle: 'Oracle guidance',
      description: 'Get a clear yes or no answer',
      icon: HelpCircle,
      color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      path: '/tools/yesorno',
      category: 'Tarot'
    },
    {
      id: 'retrogrades',
      title: 'Planetary Retrogrades',
      subtitle: 'Astrological events',
      description: 'Check the current retrograde status of planets',
      icon: RotateCcw,
      color: 'var(--mobile-primary)',
      path: '/retrogrades',
      category: 'Astrology'
    },
    {
      id: 'solarreturnchart',
      title: 'Solar Return Chart',
      subtitle: 'Personal year overview',
      description: 'View your personal year\'s astrological overview',
      icon: Sun,
      color: 'var(--mobile-primary)',
      path: '/solarreturn',
      category: 'Astrology'
    },
    {
      id: 'saturnreturn',
      title: 'Saturn Return',
      subtitle: 'Major life milestone',
      description: 'Find your Saturn return date and chart',
      icon: Sun,
      color: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
      path: '/saturnreturn',
      category: 'Astrology'
    },
    {
      id: 'lunarreturn',
      title: 'Lunar Return',
      subtitle: 'Monthly cycles',
      description: 'Find your lunar return date and chart',
      icon: Moon,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/lunarreturn',
      category: 'Astrology'
    },
    {
      id: 'transits',
      title: 'Transit Dashboard',
      subtitle: 'Current influences',
      description: 'View current planetary transits and their impact on your life',
      icon: Sparkles,
      color: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
      path: '/transits',
      category: 'Astrology'
    }
  ];

  const categories = ['All', 'Astrology', 'Tarot', 'Runes', 'Cards', 'Numerology'];

  const filteredTools = selectedCategory === 'All' 
    ? toolsList 
    : toolsList.filter(tool => tool.category === selectedCategory);

  return (
    <div className="tools-grid-modern">
      <div className="category-filters">
        {categories.map((category) => (
          <button
            key={category}
            className={`category-filter ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="tools-categories">
        {filteredTools.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <Link 
              key={tool.id} 
              to={tool.path} 
              className="tools-category-card"
            >
              <div 
                className="category-background"
                style={{ background: tool.color }}
              >
                <div className="category-overlay"></div>
              </div>
              
              <div className="category-content">
                <div className="category-icon">
                  <IconComponent size={32} />
                </div>
                
                <div className="category-text">
                  <div className="category-title">{tool.title}</div>
                  <div className="category-subtitle">{tool.subtitle}</div>
                  <div className="category-description">{tool.description}</div>
                </div>
                
                <div className="category-arrow">
                  <ArrowRight size={20} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ToolsGridModern; 
